import { Trans, useLingui } from '@lingui/react/macro'
import { LucideIcon } from 'components/LucideIcon'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'designSystem/components/AlertDialog'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'designSystem/components/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { Input } from 'designSystem/components/Input'
import { SidebarMenuAction } from 'designSystem/components/Sidebar'
import { useIsMobile } from 'designSystem/hooks/useMobile'
import { useSheetPortalHost } from 'designSystem/hooks/useSheetPortalHost'
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useState,
} from 'react'

interface AgenticChatHistorySessionOverflowMenuProps {
  isArchived?: boolean
  isPinned?: boolean
  onArchive: () => void
  onDelete: () => void
  onPin?: () => void
  onRename: (title: string) => void
  onRetry?: () => void
  onUnarchive?: () => void
  persistError?: boolean
  sessionTitle: string
}

export function AgenticChatHistorySessionOverflowMenu({
  isArchived = false,
  isPinned = false,
  onArchive,
  onDelete,
  onPin,
  onRename,
  onRetry,
  onUnarchive,
  persistError = false,
  sessionTitle,
}: AgenticChatHistorySessionOverflowMenuProps) {
  const { t } = useLingui()
  const isMobile = useIsMobile()
  const portalHost = useSheetPortalHost(isMobile)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [titleDraft, setTitleDraft] = useState(sessionTitle)

  const handleStartRename = useCallback(
    (event: Event) => {
      event.preventDefault()
      setIsMenuOpen(false)
      setTitleDraft(sessionTitle)
      window.setTimeout(() => setIsRenaming(true), 0)
    },
    [sessionTitle]
  )

  const handleStartDelete = useCallback((event: Event) => {
    event.preventDefault()
    setIsMenuOpen(false)
    window.setTimeout(() => setIsDeleting(true), 0)
  }, [])

  const handleTitleDraftChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTitleDraft(event.target.value)
    },
    []
  )

  const handleConfirmRename = useCallback(() => {
    const title = titleDraft.trim()
    if (title === '') return
    onRename(title)
    setIsRenaming(false)
  }, [onRename, titleDraft])

  const handleCancelRename = useCallback(() => {
    setIsRenaming(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    onDelete()
    setIsDeleting(false)
  }, [onDelete])

  const handleRenameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleConfirmRename()
      }
    },
    [handleConfirmRename]
  )

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction asChild placement='center' showOnHover>
            <Button
              aria-label={t`More actions for ${sessionTitle}`}
              size='icon-sm'
              type='button'
              variant='ghostMuted'>
              <LucideIcon aria-hidden='true' name='EllipsisVertical' />
            </Button>
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        {portalHost.hostNode}
        <DropdownMenuContent
          align='start'
          side={isMobile ? 'bottom' : 'right'}
          width='sm'
          container={portalHost.container}>
          {persistError && onRetry ? (
            <>
              <DropdownMenuItem onSelect={onRetry}>
                <LucideIcon aria-hidden='true' name='RotateCw' />
                <Trans>Retry</Trans>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}
          {!isArchived && onPin ? (
            <DropdownMenuItem onSelect={onPin}>
              <LucideIcon
                aria-hidden='true'
                name={isPinned ? 'PinOff' : 'Pin'}
              />
              {isPinned ? t`Unpin` : t`Pin`}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={handleStartRename}>
            <LucideIcon aria-hidden='true' name='Pencil' />
            <Trans>Rename</Trans>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isArchived ? (
            <DropdownMenuItem onSelect={onUnarchive}>
              <LucideIcon aria-hidden='true' name='ArchiveRestore' />
              <Trans>Unarchive</Trans>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={onArchive}>
              <LucideIcon aria-hidden='true' name='Archive' />
              <Trans>Archive</Trans>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem variant='destructive' onSelect={handleStartDelete}>
            <LucideIcon aria-hidden='true' name='Trash2' />
            <Trans>Delete</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={isRenaming} onOpenChange={setIsRenaming}>
        <DialogContent size='sm'>
          <DialogHeader>
            <DialogTitle>
              <Trans>Rename chat</Trans>
            </DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={titleDraft}
            onChange={handleTitleDraftChange}
            onKeyDown={handleRenameKeyDown}
          />
          <DialogFooter>
            <Button type='button' variant='ghost' onClick={handleCancelRename}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type='button'
              disabled={titleDraft.trim() === ''}
              onClick={handleConfirmRename}>
              <Trans>Rename</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={isDeleting} onOpenChange={setIsDeleting}>
        <AlertDialogContent size='sm'>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete chat?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>This chat will be removed from your history.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              onClick={handleConfirmDelete}>
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
